

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewItineraryDayModal from 'components/NewItineraryDayModal';
import MDBadge from "components/MDBadge";

const ItineraryDays = () => {
    const [itineraryDays, setItineraryDays] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('itinerary_days');

    useEffect(() => {
        fetchItineraryDays();
    }, []);

    const fetchItineraryDays = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/itinerary-days/');
            setItineraryDays(response.data);
        } catch (error) {
            console.error('Error fetching itinerary days:', error);
            setSnackbar({ open: true, message: 'Error loading Itinerary Days', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveItineraryDay = async (payload) => {
        try {
            await axiosInstance.post('/itinerary-days/', payload);
            setSnackbar({ open: true, message: 'Itinerary Day created successfully', severity: 'success' });
            fetchItineraryDays();
        } catch (error) {
            console.error('Error saving Itinerary Day:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/itinerary-days/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Title",
            accessor: "title",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value || '-'}
                </MDTypography>
            )
        },
        { Header: "Day #", accessor: "day_number" },
        {
            Header: "Description",
            accessor: "description",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="text">
                    {value || '-'}
                </MDTypography>
            )
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: itineraryDays,
    }), [itineraryDays]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Itinerary Days</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Day
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : itineraryDays.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Itinerary Day" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewItineraryDayModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveItineraryDay}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default ItineraryDays;
